import type { AuthProvider } from '@nicheaim/nicheaim-timetracking-webcomponent/tracker';
import type { Router } from '@remix-run/router';
import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getTenantCodeFromCareflowToken } from 'src/careflow/ph-core/authentication/util/token';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { canRenderIcomx } from 'src/sections/crs/patient/components/icomx/AuthenticatedIcomxChatProvider';
import { useIcomxAvailability } from 'src/sections/crs/patient/components/icomx/useIcomxAvailability';
import { getNicheAimTimeTrackingConfig } from 'src/services/api/nicheaim-time-tracking/actions';
import { createNicheAimTimeTrackingAuthProvider } from 'src/services/api/nicheaim-time-tracking/auth-provider';

import NicheAimTimeTrackingStyles from './NicheAimTimeTrackingStyles';

interface IntegrationState {
  enabled: boolean;
  loading: boolean;
  error: boolean;
  serverUrl: string | null;
  authProvider?: AuthProvider;
  tenantCode: string | null;
  userId?: string;
}
const IntegrationContext = createContext<IntegrationState>({
  enabled: false,
  loading: false,
  error: false,
  serverUrl: null,
  tenantCode: null,
});

export function NicheAimTimeTrackingProvider({
  router,
  children,
}: {
  router: Router;
  children: ReactNode;
}) {
  const availability = useIcomxAvailability(router);
  const { getCurrentUser } = useAuth();
  const userId = getCurrentUser()?.id;
  const tenantCode = getTenantCodeFromCareflowToken() ?? null;
  const translate = useTranslations('nicheaimTimeTracking', 'translations');
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const authProvider = useMemo<AuthProvider>(
    () => createNicheAimTimeTrackingAuthProvider(() => translateRef.current('tokenRequestLimited')),
    // Host exchange/cooldown is scoped to this authenticated account; elements own their runtimes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantCode, userId]
  );
  const authenticated = canRenderIcomx(availability);
  const config = useQuery(
    ['nicheaim-time-tracking-config', tenantCode, userId],
    getNicheAimTimeTrackingConfig,
    {
      enabled: authenticated && !!tenantCode && !!userId,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      retry: false,
      refetchOnWindowFocus: true,
    }
  );
  const serverUrl =
    authenticated && !config.isError && config.data?.enabled ? config.data.apiBaseUrl : null;
  const state = useMemo<IntegrationState>(
    () => ({
      enabled: !!serverUrl,
      loading: authenticated && config.isLoading,
      error: authenticated && config.isError,
      serverUrl,
      authProvider,
      tenantCode,
      userId,
    }),
    [serverUrl, authenticated, config.isLoading, config.isError, authProvider, tenantCode, userId]
  );
  return (
    <IntegrationContext.Provider value={state}>
      <NicheAimTimeTrackingStyles />
      {children}
    </IntegrationContext.Provider>
  );
}

export const useNicheAimTimeTracking = () => useContext(IntegrationContext);
